import React from 'react';
import { MapPin } from 'lucide-react';
import { ROUTE_CONFIGS } from '../routes';
import { GUIDE_CONFIGS } from '../guides';

// Pure, prerenderable 404 body. PURITY: may import only react, lucide-react,
// and other src/seo modules. No react-router, supabase, window/document, or
// CSS/image imports — styling is Tailwind class strings only.
//
// Props:
//   LinkComponent — ({ href, className, children }) => element. Defaults to
//                   plain <a> for prerender; runtime passes a Router Link adapter.

const DefaultLink = ({ href, className, children }) => (
  <a href={href} className={className}>
    {children}
  </a>
);

const linkClass =
  'text-[#A44432] underline underline-offset-2 hover:text-[#8a3829] transition-colors';

const NotFoundContent = ({ LinkComponent = DefaultLink }) => {
  const Link = LinkComponent;

  return (
    <div className="min-h-screen bg-[#FAF3E0] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-[#A44432] rounded-xl flex items-center justify-center shadow-sm">
            <MapPin className="w-5 h-5 text-white" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#2D1E17] leading-tight">
              That page doesn&apos;t exist
            </h1>
            <p className="mt-2 text-[#4A3C33] leading-relaxed">
              The link may be outdated or mistyped. Here are the pages most people are looking for:
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8DED1] shadow-sm p-6 sm:p-8 space-y-8 text-sm text-[#2D1E17]">
          <section>
            <h2 className="font-serif font-bold text-lg text-[#2D1E17] mb-3">Book a trip</h2>
            <ul className="space-y-2">
              <li>
                <Link href="/bus" className={linkClass}>
                  Shuttle bus tickets
                </Link>
              </li>
              <li>
                <Link href="/schedule" className={linkClass}>
                  Shuttle timetable
                </Link>
              </li>
              <li>
                <Link href="/private-transfer" className={linkClass}>
                  Private transfers
                </Link>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif font-bold text-lg text-[#2D1E17] mb-3">Shuttle routes</h2>
            <ul className="space-y-2">
              {ROUTE_CONFIGS.map((route) => (
                <li key={route.slug}>
                  <Link href={`/${route.slug}`} className={linkClass}>
                    {route.breadcrumbLabel}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-serif font-bold text-lg text-[#2D1E17] mb-3">Travel guides</h2>
            <ul className="space-y-2">
              {GUIDE_CONFIGS.map((guide) => (
                <li key={guide.slug}>
                  <Link href={`/${guide.slug}`} className={linkClass}>
                    {guide.breadcrumbLabel}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-serif font-bold text-lg text-[#2D1E17] mb-3">Policies &amp; contact</h2>
            <ul className="space-y-2">
              <li>
                <Link href="/booking-conditions" className={linkClass}>
                  Booking conditions
                </Link>
              </li>
              <li>
                <Link href="/offices" className={linkClass}>
                  Offices &amp; contact
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default NotFoundContent;
